/**
 * Composable for formatting and calculation utilities
 * Converted from mixins/helpers.js to Composition API
 */

const MI = 1609.344; // meters per mile
const FT = 3.28084;  // feet per meter

/**
 * Format a number with comma separators
 * @param {number|string} n - Number to format
 * @returns {string} Formatted number with commas
 */
export function numberWithCommas(n) {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Convert seconds to human-readable time format (Xh:Xm:Xs)
 * @param {number} d - Duration in seconds
 * @returns {string} Formatted time string
 */
export function secondsToHms(d) {
  d = Number(d);
  const h = Math.floor(d / 3600);
  const m = Math.floor(d % 3600 / 60);
  const s = Math.floor(d % 3600 % 60);

  const hDisplay = h > 0 ? h + 'h:' : '';
  const mDisplay = m > 0 ? m + 'm:' : '';
  const sDisplay = s > 0 ? s + 's' : '';
  return hDisplay + mDisplay + sDisplay;
}

/**
 * Get the unit label for a challenge type
 * @param {string} challengeType - 'distance', 'elevation', or 'time'
 * @returns {string} Unit label
 */
export function getUnit(challengeType) {
  switch (challengeType) {
  case 'distance':
    return 'miles';
  case 'elevation':
    return 'feet';
  case 'time':
    return 'minutes';
  default:
    return '';
  }
}

/**
 * Get the formatted total for an athlete based on challenge type
 * @param {Object} item - Athlete data with distance, elevation, time
 * @param {string} challengeType - 'distance', 'elevation', or 'time'
 * @returns {string} Formatted total
 */
export function getTotal(item, challengeType) {
  switch (challengeType) {
  case 'distance':
    return parseFloat(item.distance / MI).toFixed(2);
  case 'elevation':
    return numberWithCommas(parseFloat(item.elevation * FT).toFixed(2));
  case 'time':
    return parseFloat(item.time / 60).toFixed(0);
  default:
    return '0';
  }
}

/**
 * Calculate progress percentage towards a goal
 * @param {number} goal - Target goal value
 * @param {string} challengeType - 'distance', 'elevation', or 'time'
 * @param {Object} athleteTotal - Athlete's totals with distance, elevation, time
 * @returns {number} Progress percentage
 */
export function getProgress(goal, challengeType, athleteTotal) {
  const totalMi = athleteTotal.distance / MI;
  const totalEl = athleteTotal.elevation * FT;
  const totalTi = athleteTotal.time / 60;

  switch (challengeType) {
  case 'distance':
    return (totalMi / goal) * 100;
  case 'elevation':
    return (totalEl / goal) * 100;
  case 'time':
    return (totalTi / goal) * 100;
  default:
    return 0;
  }
}

/**
 * Get ordinal suffix for a number (1st, 2nd, 3rd, etc.)
 * @param {number} n - Number
 * @returns {string} Ordinal suffix
 */
export function getOrdinal(n) {
  if (n > 3 && n < 21) return 'th';
  switch (n % 10) {
  case 1: return 'st';
  case 2: return 'nd';
  case 3: return 'rd';
  default: return 'th';
  }
}

/**
 * Composable hook that returns all formatter functions
 * @returns {Object} Object containing all formatter functions
 */
export function useFormatters() {
  return {
    numberWithCommas,
    secondsToHms,
    getUnit,
    getTotal,
    getProgress,
    getOrdinal,
    MI,
    FT,
  };
}

export default useFormatters;
